import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import type { Tables } from '@/types/database';

/**
 * Hooks for per-transaction sharing (migration 79). A super_admin attaches
 * one or more profiles to a single transaction so they can see it on
 * "My expenses" and fill in the explanation / category / receipt — on top of
 * the cardholder default (card_assignments).
 *
 * RLS:
 *   - read  : a user can read rows where profile_id = auth.uid(); super_admin
 *             reads every row in the org.
 *   - write : super_admin only (INSERT / DELETE).
 */

const KEY = ['transaction-assignments'];

export type TransactionAssignmentRow = Tables<'transaction_assignments'> & {
  profiles: Pick<
    Tables<'profiles'>,
    'id' | 'full_name' | 'email' | 'avatar_url' | 'role'
  > | null;
};

/**
 * Every profile shared onto a given transaction (with the joined assignee
 * profile). Super_admin gets the full roster; everyone else gets at most
 * their own row back (RLS), which is enough to tell "is this shared with me".
 */
export function useTransactionAssignments(transactionId: string | undefined) {
  const { loading } = useAuth();
  return useQuery({
    queryKey: [...KEY, transactionId],
    enabled: !loading && !!transactionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transaction_assignments')
        .select(
          // transaction_assignments has TWO foreign keys to profiles
          // (profile_id and assigned_by), so we pin the embed to the
          // profile_id FK explicitly to avoid PostgREST's ambiguous-embed
          // error (PGRST201) — same pattern as card_assignments.
          'transaction_id, profile_id, organization_id, assigned_by, created_at, profiles!transaction_assignments_profile_id_fkey(id, full_name, email, avatar_url, role)',
        )
        .eq('transaction_id', transactionId!);
      if (error) throw error;
      return (data ?? []) as unknown as TransactionAssignmentRow[];
    },
  });
}

export function useAssignTransaction() {
  const qc = useQueryClient();
  const { user, profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { transaction_id: string; profile_id: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const { data, error } = await supabase
        .from('transaction_assignments')
        .insert({
          transaction_id: input.transaction_id,
          profile_id: input.profile_id,
          organization_id: profile.organization_id,
          assigned_by: user?.id ?? null,
        })
        .select('transaction_id, profile_id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...KEY, variables.transaction_id] });
      // The newly-shared person now sees this row on My expenses; refresh the
      // list caches so it shows up without a hard reload.
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
    },
  });
}

export function useUnassignTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { transaction_id: string; profile_id: string }) => {
      const { error } = await supabase
        .from('transaction_assignments')
        .delete()
        .eq('transaction_id', input.transaction_id)
        .eq('profile_id', input.profile_id);
      if (error) throw error;
      return { transaction_id: input.transaction_id, profile_id: input.profile_id };
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: [...KEY, variables.transaction_id] });
      qc.invalidateQueries({ queryKey: ['expense-transactions'] });
    },
  });
}
